/**
 * GitHubRenderer — structured rendering for GitHub issues, PRs, and lists.
 */

import { GitMerge, CircleDot, User, Calendar, Tag, MessageSquare, GitBranch } from 'lucide-react'
import { MessageContent } from '../MessageContent'
import { SupplementSections } from './SupplementSections'

interface GitHubIssue {
  number?: number
  title?: string
  body?: string
  state?: string
  user?: { login?: string; avatar_url?: string } | string
  labels?: Array<{ name?: string; color?: string }> | string[]
  assignees?: Array<{ login?: string }> | string[]
  created_at?: string
  updated_at?: string
  closed_at?: string
  comments?: number
  html_url?: string
  milestone?: { title?: string } | string | null
  [k: string]: unknown
}

interface GitHubPR extends GitHubIssue {
  merged?: boolean
  merged_at?: string
  head?: { ref?: string; sha?: string }
  base?: { ref?: string }
  additions?: number
  deletions?: number
  changed_files?: number
  mergeable?: boolean
  draft?: boolean
  review_comments?: number
}

function str(val: unknown): string {
  if (!val) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'object' && val !== null) {
    if ('login' in val) return (val as { login?: string }).login || ''
    if ('name' in val) return (val as { name?: string }).name || ''
    if ('title' in val) return (val as { title?: string }).title || ''
  }
  return String(val)
}

function stateColor(state: string, merged?: boolean): string {
  if (merged) return 'bg-accent-soft text-accent border-accent/30'
  const s = state.toLowerCase()
  if (s === 'open') return 'bg-success-soft text-success-foreground border-success/30'
  if (s === 'closed') return 'bg-destructive-soft text-destructive-foreground border-destructive/30'
  return 'bg-foreground/[0.06] text-foreground border-subtle'
}

function StateIcon({ state, merged }: { state: string; merged?: boolean }) {
  if (merged) return <GitMerge className="w-4 h-4 text-accent" />
  if (state.toLowerCase() === 'open') return <CircleDot className="w-4 h-4 text-success-foreground" />
  return <CircleDot className="w-4 h-4 text-destructive" />
}

// ---------------------------------------------------------------------------
// Single Issue
// ---------------------------------------------------------------------------

export function GitHubIssueRenderer({ data }: { data: Record<string, unknown> }) {
  const issue = data as unknown as GitHubIssue
  const user = str(issue.user)
  const state = issue.state || 'open'

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <StateIcon state={state} />
          <span className="text-xs font-mono text-muted-foreground">#{issue.number}</span>
          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${stateColor(state)}`}>
            {state}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-foreground leading-snug">{issue.title}</h3>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3">
        {user && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Author</div>
              <div className="text-foreground">{user}</div>
            </div>
          </div>
        )}
        {issue.assignees && issue.assignees.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Assignees</div>
              <div className="text-foreground">{issue.assignees.map(a => str(a)).join(', ')}</div>
            </div>
          </div>
        )}
        {issue.created_at && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Created</div>
              <div className="text-foreground">{new Date(issue.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        )}
        {issue.comments !== undefined && (
          <div className="flex items-center gap-2 text-xs">
            <MessageSquare className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Comments</div>
              <div className="text-foreground">{issue.comments}</div>
            </div>
          </div>
        )}
      </div>

      {/* Labels */}
      {issue.labels && issue.labels.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Labels
          </div>
          <div className="flex flex-wrap gap-1.5">
            {issue.labels.map((label, i) => {
              const name = typeof label === 'string' ? label : label.name || ''
              const color = typeof label === 'object' && label?.color ? `#${label.color}` : undefined
              return (
                <span
                  key={i}
                  className="rounded-full px-2.5 py-0.5 text-xs font-medium border"
                  style={color ? {
                    backgroundColor: `${color}20`,
                    borderColor: `${color}60`,
                    color: color,
                  } : undefined}
                >
                  {name}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* Body */}
      {issue.body && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5">Body</div>
          <div className="rounded-lg bg-foreground/[0.03] border border-subtle p-3 text-xs text-foreground leading-relaxed max-h-64 overflow-y-auto">
            <MessageContent content={issue.body} />
          </div>
        </div>
      )}

      {/* Merged supplements (comments, etc.) */}
      <SupplementSections data={data} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pull Request
// ---------------------------------------------------------------------------

export function GitHubPRRenderer({ data }: { data: Record<string, unknown> }) {
  const pr = data as unknown as GitHubPR
  const state = pr.state || 'open'
  const merged = pr.merged || !!pr.merged_at

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <StateIcon state={state} merged={merged} />
          <span className="text-xs font-mono text-muted-foreground">#{pr.number}</span>
          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${stateColor(state, merged)}`}>
            {merged ? 'Merged' : state}
          </span>
          {pr.draft && (
            <span className="rounded-full bg-foreground/[0.06] border border-subtle px-2 py-0.5 text-xs text-muted-foreground">Draft</span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground leading-snug">{pr.title}</h3>
      </div>

      {/* Branch info */}
      {pr.head?.ref && pr.base?.ref && (
        <div className="flex items-center gap-2 text-xs bg-foreground/[0.04] border border-subtle rounded-lg px-3 py-2">
          <GitBranch className="w-3.5 h-3.5 text-subtle-foreground" />
          <code className="text-accent font-medium">{pr.head.ref}</code>
          <span className="text-subtle-foreground">into</span>
          <code className="text-info-foreground font-medium">{pr.base.ref}</code>
        </div>
      )}

      {/* Stats */}
      {(pr.additions !== undefined || pr.deletions !== undefined || pr.changed_files !== undefined) && (
        <div className="flex items-center gap-4 text-xs">
          {pr.changed_files !== undefined && (
            <span className="text-muted-foreground">{pr.changed_files} files changed</span>
          )}
          {pr.additions !== undefined && (
            <span className="text-success-foreground font-medium">+{pr.additions}</span>
          )}
          {pr.deletions !== undefined && (
            <span className="text-destructive-foreground font-medium">-{pr.deletions}</span>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3">
        {str(pr.user) && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Author</div>
              <div className="text-foreground">{str(pr.user)}</div>
            </div>
          </div>
        )}
        {pr.created_at && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Created</div>
              <div className="text-foreground">{new Date(pr.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Labels */}
      {pr.labels && pr.labels.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Labels
          </div>
          <div className="flex flex-wrap gap-1.5">
            {pr.labels.map((label, i) => {
              const name = typeof label === 'string' ? label : label.name || ''
              return (
                <span key={i} className="rounded-full bg-foreground/[0.06] border border-subtle px-2.5 py-0.5 text-xs text-foreground font-medium">
                  {name}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* Body */}
      {pr.body && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5">Description</div>
          <div className="rounded-lg bg-foreground/[0.03] border border-subtle p-3 text-xs text-foreground leading-relaxed max-h-64 overflow-y-auto">
            <MessageContent content={pr.body} />
          </div>
        </div>
      )}

      {/* Merged supplements (reviews, comments, commits, files) */}
      <SupplementSections data={data} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Multi-Item List (issues, PRs, repos, etc.)
// ---------------------------------------------------------------------------

export function GitHubItemsRenderer({ data }: { data: unknown[] }) {
  const items = data as GitHubIssue[]

  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const state = item.state || ''
        const user = str(item.user)

        return (
          <div key={item.number || i} className="rounded-lg border border-subtle bg-foreground/[0.03] p-3 hover:border-accent/30 transition">
            <div className="flex items-start gap-2">
              {state && <StateIcon state={state} />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  {item.number && <span className="text-xs font-mono text-muted-foreground">#{item.number}</span>}
                  {state && (
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${stateColor(state)}`}>
                      {state}
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground font-medium line-clamp-2">{String(item.title || (item as Record<string, unknown>).name || (item as Record<string, unknown>).full_name || '')}</p>
                {typeof (item as Record<string, unknown>).description === 'string' && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{(item as Record<string, string>).description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              {user && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {user}</span>}
              {item.updated_at && <span>{new Date(item.updated_at).toLocaleDateString()}</span>}
              {item.comments !== undefined && (
                <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {item.comments}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
